import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Zap, Clock, Hash, BarChart3, Send, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from '../hooks/use-toast';
import api from '../utils/api';

const MODELS = [
  { id: 'opus-4.7', name: 'Claude Opus 4.7', color: 'bg-orange-500', textColor: 'text-orange-400', borderColor: 'border-orange-500/30' },
  { id: 'sonnet-4.7', name: 'Claude Sonnet 4.7', color: 'bg-violet-500', textColor: 'text-violet-400', borderColor: 'border-violet-500/30' },
  { id: 'openai', name: 'GPT-5.2', color: 'bg-emerald-500', textColor: 'text-emerald-400', borderColor: 'border-emerald-500/30' },
  { id: 'o3', name: 'OpenAI o3', color: 'bg-green-500', textColor: 'text-green-400', borderColor: 'border-green-500/30' },
  { id: 'gemini', name: 'Gemini 3.1 Pro', color: 'bg-sky-500', textColor: 'text-sky-400', borderColor: 'border-sky-500/30' },
  { id: 'claude', name: 'Claude Sonnet 4', color: 'bg-purple-500', textColor: 'text-purple-400', borderColor: 'border-purple-500/30' },
  { id: 'grok', name: 'Grok (GPT-4.1)', color: 'bg-amber-500', textColor: 'text-amber-400', borderColor: 'border-amber-500/30' },
];

const ComparePage = () => {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const [selectedModels, setSelectedModels] = useState(['opus-4.7', 'openai', 'gemini']);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState(null);

  const toggleModel = (id) => {
    setSelectedModels(prev => {
      if (prev.includes(id)) {
        if (prev.length <= 2) return prev;
        return prev.filter(m => m !== id);
      }
      return [...prev, id];
    });
  };

  const handleCompare = async () => {
    if (!prompt.trim() || selectedModels.length < 2) return;
    setLoading(true);
    setResults(null);
    setMeta(null);
    try {
      const res = await api.post('/ai/compare', {
        prompt: prompt.trim(),
        models: selectedModels,
      });
      setResults(res.data.results);
      setMeta({ fastest: res.data.fastest, longest: res.data.longest, credits_used: res.data.credits_used, credits_remaining: res.data.credits_remaining });
    } catch (err) {
      toast({ title: 'Comparison Failed', description: err.response?.data?.detail || 'Error running comparison', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-3 text-muted-foreground hover:text-foreground hover:bg-accent" data-testid="compare-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-blue-600 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-foreground" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground" data-testid="compare-title">AI Model Comparison</h1>
              <p className="text-sm text-muted-foreground">Run the same prompt against multiple models side-by-side</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Prompt Input */}
        <Card className="bg-card border-border">
          <CardContent className="p-5 space-y-4">
            <Textarea
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              placeholder="Enter your prompt to compare across models..."
              className="min-h-[80px] bg-background border-input text-foreground placeholder:text-muted-foreground focus:border-blue-500/50"
              data-testid="compare-prompt-input"
            />
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {MODELS.map(m => (
                  <button
                    key={m.id}
                    onClick={() => toggleModel(m.id)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
                      selectedModels.includes(m.id)
                        ? `${m.borderColor} ${m.textColor} bg-secondary`
                        : 'border-input text-muted-foreground hover:text-foreground/80'
                    }`}
                    data-testid={`compare-model-toggle-${m.id}`}
                  >
                    <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${selectedModels.includes(m.id) ? m.color : 'bg-slate-600'}`} />
                    {m.name}
                  </button>
                ))}
              </div>
              <Button
                onClick={handleCompare}
                disabled={loading || !prompt.trim() || selectedModels.length < 2}
                className="bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-500 hover:to-blue-500 gap-2"
                data-testid="compare-run-btn"
              >
                {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {loading ? 'Comparing...' : `Compare (${selectedModels.length} credits)`}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Meta Stats */}
        {meta && (
          <div className="flex items-center gap-4" data-testid="compare-meta">
            <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 gap-1">
              <Zap className="w-3 h-3" /> {meta.credits_used} credits used
            </Badge>
            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 gap-1">
              <Clock className="w-3 h-3" /> Fastest: {MODELS.find(m => m.id === meta.fastest)?.name}
            </Badge>
            <Badge className="bg-violet-500/20 text-violet-400 border-violet-500/30 gap-1">
              <Hash className="w-3 h-3" /> Most detailed: {MODELS.find(m => m.id === meta.longest)?.name}
            </Badge>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className={`grid gap-4 ${selectedModels.length === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
            {selectedModels.map(id => {
              const m = MODELS.find(mo => mo.id === id);
              return (
                <Card key={id} className="bg-card border-border">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <span className={`w-3 h-3 rounded-full ${m?.color} animate-pulse`} />
                      <span className="text-foreground font-semibold text-sm">{m?.name}</span>
                    </div>
                    <div className="space-y-2 animate-pulse">
                      <div className="h-3 bg-[#222222] rounded w-full" />
                      <div className="h-3 bg-accent rounded w-5/6" />
                      <div className="h-3 bg-secondary rounded w-4/6" />
                      <div className="h-3 bg-accent rounded w-full" />
                      <div className="h-3 bg-secondary rounded w-3/4" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Results */}
        {results && (
          <div className={`grid gap-4 ${results.length === 3 ? 'grid-cols-3' : 'grid-cols-2'}`} data-testid="compare-results">
            {results.map(r => {
              const m = MODELS.find(mo => mo.id === r.model_id);
              const isFastest = meta?.fastest === r.model_id;
              const isLongest = meta?.longest === r.model_id;
              return (
                <Card key={r.model_id} className={`bg-card border-border ${r.status === 'error' ? 'border-red-500/30' : ''}`} data-testid={`compare-result-${r.model_id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className={`w-3 h-3 rounded-full ${m?.color}`} />
                        <span className="text-foreground font-semibold text-sm">{r.name}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {isFastest && <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs">Fastest</Badge>}
                        {isLongest && <Badge className="bg-violet-500/20 text-violet-400 border-violet-500/30 text-xs">Most detailed</Badge>}
                      </div>
                    </div>

                    {/* Stats Row */}
                    <div className="flex items-center gap-3 mb-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {r.response_time}s</span>
                      <span className="flex items-center gap-1"><Hash className="w-3 h-3" /> {r.word_count} words</span>
                      <span className="flex items-center gap-1"><Zap className="w-3 h-3" /> ${r.cost}</span>
                    </div>

                    {/* Response Content */}
                    <div className="max-h-[400px] overflow-y-auto pr-2 text-sm text-foreground/80 whitespace-pre-wrap leading-relaxed" data-testid={`compare-content-${r.model_id}`}>
                      {r.content}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ComparePage;
